import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle, Network, RefreshCw } from "lucide-react";
import { graphApi, domainsApi } from "../lib/api";
import { AssetGraph } from "../components/graph";

export default function Graph() {
  const [searchParams, setSearchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

  // Get domain_id from URL params
  const domainId = searchParams.get("domain");
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(domainId);

  // Fetch available domains for the selector
  const { data: domains } = useQuery({
    queryKey: ["domains"],
    queryFn: domainsApi.list,
  });

  // Fetch graph data
  const {
    data: graphData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["asset-graph", selectedDomainId],
    queryFn: () =>
      selectedDomainId
        ? graphApi.getDomainGraph(selectedDomainId)
        : graphApi.getGraph({ max_nodes: 1000 }),
    staleTime: 30_000, // Cache for 30 seconds
  });

  // Update URL when domain changes
  useEffect(() => {
    if (selectedDomainId) {
      setSearchParams({ domain: selectedDomainId });
    } else {
      setSearchParams({});
    }
  }, [selectedDomainId, setSearchParams]);

  // Measure container size
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.max(width, 400),
          height: Math.max(height - 20, 400), // Account for padding
        });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Network className="h-6 w-6 text-primary" />
            Asset Graph
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Visualize relationships between assets in your attack surface
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Domain Selector */}
          <select
            value={selectedDomainId || ""}
            onChange={(e) => setSelectedDomainId(e.target.value || null)}
            className="px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All Domains</option>
            {domains?.map((domain) => (
              <option key={domain.id} value={domain.id}>
                {domain.domain}
              </option>
            ))}
          </select>

          {/* Refresh Button */}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div ref={containerRef} className="flex-1 p-4 min-h-0">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading asset graph...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <div className="text-center">
              <p className="font-medium text-destructive">Failed to load graph</p>
              <p className="text-sm text-muted-foreground mt-1">
                {error instanceof Error ? error.message : "An error occurred"}
              </p>
            </div>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        ) : graphData && graphData.nodes.length > 0 ? (
          <AssetGraph
            data={graphData}
            width={dimensions.width}
            height={dimensions.height}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <Network className="h-12 w-12 text-muted-foreground" />
            <div className="text-center">
              <p className="font-medium">No assets found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedDomainId
                  ? "This domain has no discovered assets yet. Run a scan to discover assets."
                  : "Run a scan to start discovering assets for your domains."}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {graphData && graphData.nodes.length > 0 && (
        <div className="px-6 py-3 border-t border-border bg-muted/30">
          <div className="flex items-center gap-6 text-sm">
            <span className="text-muted-foreground">
              Total Nodes:{" "}
              <span className="font-medium text-foreground">
                {graphData.metadata.total_nodes}
              </span>
            </span>
            <span className="text-muted-foreground">
              Subdomains:{" "}
              <span className="font-medium text-foreground">
                {graphData.metadata.node_counts.subdomains}
              </span>
            </span>
            <span className="text-muted-foreground">
              IPs:{" "}
              <span className="font-medium text-foreground">
                {graphData.metadata.node_counts.ips}
              </span>
            </span>
            <span className="text-muted-foreground">
              Ports:{" "}
              <span className="font-medium text-foreground">
                {graphData.metadata.node_counts.ports}
              </span>
            </span>
            {graphData.metadata.domain_name && (
              <span className="text-muted-foreground">
                Domain:{" "}
                <span className="font-medium text-foreground">
                  {graphData.metadata.domain_name}
                </span>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
